import React, { useEffect, useMemo, useState } from "react";
import { Truck, Search, Download, ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import ExcelDownloadLoader from "../../../../components/loaderTruck/ExcelDownloadLoader";

const PALETTE = {
  primary: "#09478C",
  primarySoft: "rgba(9, 71, 140, 0.08)",
  amber: "#B45309",
  amberSoft: "rgba(180, 83, 9, 0.12)",
  neutral: "#737782",
  neutralSoft: "rgba(115, 119, 130, 0.12)",
  emeraldBg: "#D1FAE5",
  emeraldText: "#065F46",
  text: "#0F1C2C",
  textVariant: "#424751",
  textMuted: "#737782",
  textFaint: "#9CA6B8",
  outlineVariant: "#C3C6D2",
  border: "#E5E7EB",
  borderSoft: "#EEF4FF",
  surface: "#FFFFFF",
  surfaceAlt: "#F8F9FF",
  surfaceContainerLow: "#EEF4FF",
  surfaceContainerHigh: "#DBE9FE",
  surfaceHighest: "#D6E4F9",
};

const SOFT_SHADOW =
  "0px 1px 3px rgba(0,0,0,0.05), 0px 10px 15px -3px rgba(0,0,0,0.05)";

const SEARCH_DEBOUNCE_MS = 400;

const listaCSS = `
@keyframes listaShimmer {
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
}
.lista-skeleton {
  background: linear-gradient(90deg, ${PALETTE.surfaceContainerLow} 0%, ${PALETTE.surfaceContainerHigh} 50%, ${PALETTE.surfaceContainerLow} 100%);
  background-size: 800px 100%;
  animation: listaShimmer 1.4s ease-in-out infinite;
  border-radius: 8px;
  display: inline-block;
}
.lista-row:hover { background: ${PALETTE.surfaceContainerLow}; }
.lista-pag-btn:hover:not(:disabled) { background: ${PALETTE.surfaceContainerLow}; }
.lista-export:hover { background: ${PALETTE.primary}; opacity: 0.9; }
.lista-expand-btn:hover { background: ${PALETTE.surfaceContainerHigh}; color: ${PALETTE.primary}; }
.lista-chevron { transition: transform .15s ease; }
.lista-chevron.open { transform: rotate(90deg); }
`;

const Skeleton = ({ width = "100%", height = 14, radius = 8, style }) => (
  <span
    className="lista-skeleton"
    style={{ width, height, borderRadius: radius, ...style }}
  />
);

const styles = {
  card: {
    background: PALETTE.surface,
    borderRadius: "16px",
    boxShadow: SOFT_SHADOW,
    overflow: "hidden",
  },
  cardHeader: {
    padding: "16px 24px",
    borderBottom: `1px solid ${PALETTE.outlineVariant}`,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "12px",
  },
  cardTitle: {
    fontSize: "20px",
    fontWeight: 600,
    margin: 0,
    color: PALETTE.text,
  },
  cardSubtitle: {
    fontSize: "14px",
    color: PALETTE.textMuted,
    marginTop: "2px",
  },
  exportBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "9px 18px",
    background: PALETTE.primary,
    border: "none",
    borderRadius: "10px",
    color: "#FFFFFF",
    fontSize: "13px",
    fontWeight: 700,
    cursor: "pointer",
    boxShadow: SOFT_SHADOW,
    transition: "background .15s",
  },
  searchWrap: {
    padding: "24px",
    borderBottom: `1px solid ${PALETTE.outlineVariant}55`,
  },
  searchBox: {
    position: "relative",
    maxWidth: "520px",
  },
  searchIcon: {
    position: "absolute",
    left: "14px",
    top: "50%",
    transform: "translateY(-50%)",
    color: PALETTE.textFaint,
    pointerEvents: "none",
    display: "inline-flex",
  },
  searchInput: {
    width: "100%",
    padding: "10px 16px 10px 42px",
    borderRadius: "12px",
    border: "none",
    fontSize: "14px",
    outline: "none",
    background: PALETTE.surfaceContainerLow,
    transition: "box-shadow .15s",
    fontFamily: "inherit",
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "14px" },
  th: (sortable, highlighted) => ({
    textAlign: "left",
    padding: "12px 24px",
    background: PALETTE.surfaceContainerLow,
    color: highlighted ? PALETTE.primary : PALETTE.textVariant,
    fontWeight: 600,
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    cursor: sortable ? "pointer" : "default",
    userSelect: "none",
    whiteSpace: "nowrap",
  }),
  expandTh: {
    padding: "12px",
    background: PALETTE.surfaceContainerLow,
    width: "40px",
  },
  expandBtn: {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    padding: "4px",
    borderRadius: "9999px",
    color: PALETTE.textMuted,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "color .15s",
  },
  expandTd: { padding: "12px", textAlign: "center", verticalAlign: "middle" },
  detailRow: {
    background: `${PALETTE.surfaceContainerLow}80`,
  },
  detailWrap: {
    padding: "8px 24px 20px 60px",
  },
  detailPanel: {
    display: "flex",
    flexWrap: "wrap",
    gap: "12px",
    background: PALETTE.surface,
    borderRadius: "12px",
    boxShadow: SOFT_SHADOW,
    padding: "16px 20px",
  },
  detailStat: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    minWidth: "150px",
  },
  detailDot: (color) => ({
    width: "10px",
    height: "10px",
    borderRadius: "9999px",
    background: color,
    flexShrink: 0,
  }),
  detailLabel: {
    fontSize: "11px",
    color: PALETTE.textMuted,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  detailValue: {
    fontSize: "15px",
    fontWeight: 700,
    color: PALETTE.text,
  },
  subTableCard: {
    marginTop: "14px",
    background: PALETTE.surface,
    borderRadius: "12px",
    boxShadow: SOFT_SHADOW,
    overflow: "hidden",
  },
  subTableCaption: {
    padding: "10px 16px",
    fontSize: "11px",
    fontWeight: 700,
    color: PALETTE.textMuted,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    borderBottom: `1px solid ${PALETTE.outlineVariant}40`,
  },
  subTable: { width: "100%", borderCollapse: "collapse", fontSize: "13px" },
  subTh: {
    textAlign: "left",
    padding: "8px 16px",
    background: PALETTE.surfaceContainerLow,
    color: PALETTE.textVariant,
    fontWeight: 700,
    fontSize: "10.5px",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    whiteSpace: "nowrap",
  },
  subTd: {
    padding: "10px 16px",
    borderBottom: `1px solid ${PALETTE.outlineVariant}30`,
    color: PALETTE.text,
    verticalAlign: "top",
  },
  subSecondaryText: {
    display: "block",
    fontSize: "11px",
    color: PALETTE.textMuted,
    marginTop: "2px",
  },
  subEmpty: {
    padding: "16px",
    textAlign: "center",
    color: PALETTE.textMuted,
    fontSize: "13px",
  },
  td: {
    padding: "16px 24px",
    borderBottom: `1px solid ${PALETTE.outlineVariant}55`,
    color: PALETTE.text,
    fontVariantNumeric: "tabular-nums",
  },
  truckCell: { display: "flex", alignItems: "center", gap: "16px" },
  truckIcon: {
    width: "32px",
    height: "32px",
    borderRadius: "6px",
    background: PALETTE.surfaceContainerHigh,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: PALETTE.primary,
    flexShrink: 0,
  },
  nombre: { fontWeight: 500 },
  rfc: {
    color: PALETTE.textMuted,
    fontFamily: "ui-monospace, Menlo, monospace",
    fontSize: "13px",
  },
  barRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    minWidth: "180px",
  },
  bar: {
    flex: 1,
    display: "flex",
    background: PALETTE.border,
    height: "8px",
    borderRadius: "9999px",
    overflow: "hidden",
    minWidth: "100px",
  },
  barFill: (pct) => ({
    width: `${pct}%`,
    height: "100%",
    background: PALETTE.primary,
    borderRadius: "9999px",
    transition: "width .4s ease",
  }),
  barSeg: (pct, color) => ({
    width: `${pct}%`,
    height: "100%",
    background: color,
    transition: "width .4s ease",
  }),
  barPct: {
    fontSize: "12px",
    fontWeight: 600,
    color: PALETTE.text,
    width: "40px",
    textAlign: "right",
  },
  assignCell: {
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "4px",
  },
  assignValue: { fontWeight: 600, color: PALETTE.text },
  pctTag: (color, soft) => ({
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: "9999px",
    background: soft,
    color,
    fontSize: "11px",
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
    letterSpacing: "0.02em",
  }),
  badge: (variant) => {
    const v =
      variant === "active"
        ? { bg: PALETTE.emeraldBg, fg: PALETTE.emeraldText }
        : { bg: PALETTE.surfaceHighest, fg: PALETTE.textMuted };
    return {
      display: "inline-block",
      padding: "4px 10px",
      borderRadius: "9999px",
      background: v.bg,
      color: v.fg,
      fontSize: "10px",
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
    };
  },
  actionBtn: {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    padding: "6px",
    borderRadius: "9999px",
    color: PALETTE.primary,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  empty: { textAlign: "center", padding: "40px", color: PALETTE.textMuted },
  footer: {
    padding: "12px 24px",
    background: PALETTE.surface,
    borderTop: `1px solid ${PALETTE.outlineVariant}`,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: "13px",
    color: PALETTE.textMuted,
  },
  pagNav: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  pagBtn: (active, disabled) => ({
    minWidth: "32px",
    height: "32px",
    padding: "0 8px",
    borderRadius: "8px",
    border: "none",
    background: active ? PALETTE.primary : "transparent",
    color: active ? "#FFFFFF" : disabled ? PALETTE.textFaint : PALETTE.text,
    fontSize: "13px",
    fontWeight: 600,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.4 : 1,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  }),
  dateFilter: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "12px",
    color: PALETTE.textMuted,
    flexWrap: "wrap",
  },
  dateLabel: {
    fontSize: "11px",
    fontWeight: 600,
    color: PALETTE.textVariant,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  dateInput: {
    padding: "6px 10px",
    border: `1px solid ${PALETTE.outlineVariant}`,
    borderRadius: "6px",
    fontSize: "12px",
    fontFamily: "inherit",
    color: PALETTE.text,
    background: PALETTE.surface,
    outline: "none",
  },
};

// Fecha local (no UTC) para que el calendario muestre el día actual aún
// cuando la hora local esté rezagada respecto a UTC.
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const fmtNum = (n) =>
  Number(n || 0).toLocaleString("es-MX", { maximumFractionDigits: 2 });
const clampPct = (n) => Math.max(0, Math.min(100, Number(n || 0)));
// Porcentaje de una parte respecto al total de servicios (misma lógica que la
// gráfica Top 10 del análisis general): si no hay servicios, 0%.
const pctOf = (part, whole) => {
  const w = Number(whole || 0);
  if (w <= 0) return 0;
  return clampPct((Number(part || 0) / w) * 100);
};

const ListaTransportistas = ({
  rows = [],
  loading = false,
  error = null,
  page = 0, // 0-based, como Spring
  size = 10,
  totalElements = 0,
  totalPages = 0,
  search = "",
  sortBy = "nombre",
  sortDirection = "asc",
  fechaDesde,
  fechaHasta,
  exporting = false,
  exportProgress = 0,
  onExport = () => {},
  onSearchChange = () => {},
  onPageChange = () => {},
  onSortChange = () => {},
  onFechaDesdeChange = () => {},
  onFechaHastaChange = () => {},
}) => {
  // Caja de búsqueda local con debounce: no se llama al API en cada tecla.
  const [searchInput, setSearchInput] = useState(search);
  // Filas desplegadas muestran el desglose de totales + los servicios reales
  // de la línea (mv.movimientos, ya incluidos en la respuesta de la tabla).
  const [expandedRows, setExpandedRows] = useState({});
  const toggleExpanded = (key) =>
    setExpandedRows((prev) => ({ ...prev, [key]: !prev[key] }));

  useEffect(() => {
    if (searchInput === search) return;
    const handler = setTimeout(
      () => onSearchChange(searchInput.trim()),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(handler);
  }, [searchInput]);

  const sortIcon = (key) => {
    if (sortBy !== key) return null;
    return (
      <span style={{ marginLeft: "4px" }}>
        {sortDirection === "asc" ? "▲" : "▼"}
      </span>
    );
  };

  const COLSPAN = 7;
  const safeTotalPages = Math.max(1, totalPages);
  const start = page * size;
  const showingFrom = totalElements === 0 ? 0 : start + 1;
  const showingTo = Math.min(start + rows.length, totalElements);

  // Ventana de páginas alrededor de la actual (evita pintar miles de botones).
  const pageWindow = useMemo(() => {
    const span = 2;
    const first = Math.max(0, page - span);
    const last = Math.min(safeTotalPages - 1, page + span);
    const arr = [];
    for (let i = first; i <= last; i++) arr.push(i);
    return arr;
  }, [page, safeTotalPages]);

  return (
    <>
      <style>{listaCSS}</style>
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <div>
            <h3 style={styles.cardTitle}>Líneas de transporte</h3>
            <div style={styles.cardSubtitle}>
              {fmtNum(totalElements)} líneas en el periodo
            </div>
          </div>
          <div style={styles.dateFilter}>
            <button
              className="lista-export"
              style={{
                ...styles.exportBtn,
                ...(exporting || totalElements === 0
                  ? { opacity: 0.5, cursor: "not-allowed" }
                  : {}),
              }}
              onClick={onExport}
              disabled={exporting || totalElements === 0}
            >
              <Download size={16} /> {exporting ? "Generando…" : "Exportar"}
            </button>
          </div>
        </div>

        <div style={styles.searchWrap}>
          <div style={styles.searchBox}>
            <span style={styles.searchIcon}>
              <Search size={18} />
            </span>
            <input
              type="text"
              placeholder="Buscar por línea de transporte..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={styles.searchInput}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = PALETTE.primary;
                e.currentTarget.style.boxShadow = `0 0 0 3px ${PALETTE.primary}33`;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = PALETTE.outlineVariant;
                e.currentTarget.style.boxShadow = "none";
              }}
            />
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.expandTh}>
                  <span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden" }}>
                    Desplegar
                  </span>
                </th>
                <th
                  style={styles.th(true, sortBy === "nombre")}
                  onClick={() => onSortChange("nombre")}
                >
                  Línea de transporte{sortIcon("nombre")}
                </th>
                <th
                  style={styles.th(true, sortBy === "totalAsignacion")}
                  onClick={() => onSortChange("totalAsignacion")}
                >
                  Total asignación{sortIcon("totalAsignacion")}
                </th>
                <th style={{ ...styles.th(false), textAlign: "center" }}>
                  Asignados transporte
                </th>
                <th style={{ ...styles.th(false), textAlign: "center" }}>
                  Asignados patios
                </th>
                <th style={{ ...styles.th(false), textAlign: "center" }}>
                  Sin asignación
                </th>
                <th style={{ ...styles.th(false), textAlign: "center" }}>
                  Total servicios
                </th>
              </tr>
            </thead>
            <tbody>
              {loading &&
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={`sk-${i}`}>
                    <td style={styles.expandTd} />
                    <td style={styles.td}>
                      <div style={styles.truckCell}>
                        <Skeleton width={32} height={32} radius={6} />
                        <Skeleton width={`${50 + ((i * 9) % 30)}%`} height={12} />
                      </div>
                    </td>
                    <td style={styles.td}>
                      <Skeleton width="100%" height={22} radius={9999} />
                    </td>
                    <td style={{ ...styles.td, textAlign: "center" }}>
                      <Skeleton width={48} height={12} />
                    </td>
                    <td style={{ ...styles.td, textAlign: "center" }}>
                      <Skeleton width={48} height={12} />
                    </td>
                    <td style={{ ...styles.td, textAlign: "center" }}>
                      <Skeleton width={48} height={12} />
                    </td>
                    <td style={{ ...styles.td, textAlign: "center" }}>
                      <Skeleton width={48} height={12} />
                    </td>
                  </tr>
                ))}

              {!loading && error && (
                <tr>
                  <td
                    colSpan={COLSPAN}
                    style={{ ...styles.td, ...styles.empty, color: "#B91C1C" }}
                  >
                    ⚠ {error}
                  </td>
                </tr>
              )}

              {!loading &&
                !error &&
                rows.map((l, idx) => {
                  const pctTransporte = pctOf(
                    l.asignadosTransporte,
                    l.totalServicios
                  );
                  const pctPatios = pctOf(l.asignadosPatios, l.totalServicios);
                  // Sin asignación = servicios que no quedaron cubiertos ni por
                  // transporte ni por patios (no baja de 0).
                  const sinAsignacion = Math.max(
                    Number(l.totalServicios || 0) -
                      (Number(l.asignadosTransporte || 0) +
                        Number(l.asignadosPatios || 0)),
                    0
                  );
                  const pctSin = pctOf(sinAsignacion, l.totalServicios);
                  const rowKey = `${l.nombre ?? "linea"}-${idx}`;
                  const isExpanded = !!expandedRows[rowKey];
                  return (
                    <React.Fragment key={rowKey}>
                    <tr className="lista-row">
                      <td style={styles.expandTd}>
                        <button
                          type="button"
                          className="lista-expand-btn"
                          style={styles.expandBtn}
                          aria-expanded={isExpanded}
                          aria-label={isExpanded ? "Ocultar desglose" : "Ver desglose"}
                          onClick={() => toggleExpanded(rowKey)}
                        >
                          <ChevronRight
                            size={18}
                            className={`lista-chevron${isExpanded ? " open" : ""}`}
                          />
                        </button>
                      </td>
                      <td style={styles.td}>
                        <div style={styles.truckCell}>
                          <span style={styles.truckIcon}>
                            <Truck size={16} />
                          </span>
                          <span style={styles.nombre}>{l.nombre || "—"}</span>
                        </div>
                      </td>
                      <td style={styles.td}>
                        <div style={styles.barRow}>
                          <div
                            style={styles.bar}
                            title={`Transporte: ${pctTransporte.toFixed(
                              1
                            )}% · Patios: ${pctPatios.toFixed(1)}%`}
                          >
                            <div
                              style={styles.barSeg(
                                pctTransporte,
                                PALETTE.primary
                              )}
                            />
                            <div
                              style={styles.barSeg(pctPatios, PALETTE.amber)}
                            />
                          </div>
                          <span style={styles.barPct}>
                            {fmtNum(l.totalAsignacion)}
                          </span>
                        </div>
                      </td>
                      <td style={{ ...styles.td, textAlign: "center" }}>
                        <span style={styles.assignCell}>
                          <span style={styles.assignValue}>
                            {fmtNum(l.asignadosTransporte)}
                          </span>
                          <span
                            style={styles.pctTag(
                              PALETTE.primary,
                              PALETTE.primarySoft
                            )}
                          >
                            {pctTransporte.toFixed(1)}%
                          </span>
                        </span>
                      </td>
                      <td style={{ ...styles.td, textAlign: "center" }}>
                        <span style={styles.assignCell}>
                          <span style={styles.assignValue}>
                            {fmtNum(l.asignadosPatios)}
                          </span>
                          <span
                            style={styles.pctTag(
                              PALETTE.amber,
                              PALETTE.amberSoft
                            )}
                          >
                            {pctPatios.toFixed(1)}%
                          </span>
                        </span>
                      </td>
                      <td style={{ ...styles.td, textAlign: "center" }}>
                        <span style={styles.assignCell}>
                          <span style={styles.assignValue}>
                            {fmtNum(sinAsignacion)}
                          </span>
                          <span
                            style={styles.pctTag(
                              PALETTE.neutral,
                              PALETTE.neutralSoft
                            )}
                          >
                            {pctSin.toFixed(1)}%
                          </span>
                        </span>
                      </td>
                      <td style={{ ...styles.td, textAlign: "center" }}>
                        {fmtNum(l.totalServicios)}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr style={styles.detailRow}>
                        <td colSpan={COLSPAN} style={{ padding: 0, border: "none" }}>
                          <div style={styles.detailWrap}>
                            <div style={styles.detailPanel}>
                              <div style={styles.detailStat}>
                                <span style={styles.detailDot(PALETTE.primary)} />
                                <div>
                                  <div style={styles.detailLabel}>Asignados transporte</div>
                                  <div style={styles.detailValue}>
                                    {fmtNum(l.asignadosTransporte)} ({pctTransporte.toFixed(1)}%)
                                  </div>
                                </div>
                              </div>
                              <div style={styles.detailStat}>
                                <span style={styles.detailDot(PALETTE.amber)} />
                                <div>
                                  <div style={styles.detailLabel}>Asignados patios</div>
                                  <div style={styles.detailValue}>
                                    {fmtNum(l.asignadosPatios)} ({pctPatios.toFixed(1)}%)
                                  </div>
                                </div>
                              </div>
                              <div style={styles.detailStat}>
                                <span style={styles.detailDot(PALETTE.neutral)} />
                                <div>
                                  <div style={styles.detailLabel}>Sin asignación</div>
                                  <div style={styles.detailValue}>
                                    {fmtNum(sinAsignacion)} ({pctSin.toFixed(1)}%)
                                  </div>
                                </div>
                              </div>
                              <div style={styles.detailStat}>
                                <div>
                                  <div style={styles.detailLabel}>Total servicios</div>
                                  <div style={styles.detailValue}>
                                    {fmtNum(l.totalServicios)}
                                  </div>
                                </div>
                              </div>
                            </div>
                            {Array.isArray(l.movimientos) && l.movimientos.length > 0 ? (
                              <div style={styles.subTableCard}>
                                <div style={styles.subTableCaption}>
                                  Servicios de esta línea ({fmtNum(l.movimientos.length)}
                                  {l.movimientos.length !== Number(l.totalServicios || 0)
                                    ? ` de ${fmtNum(l.totalServicios)}`
                                    : ""}
                                  )
                                </div>
                                <div style={{ overflowX: "auto" }}>
                                  <table style={styles.subTable}>
                                    <thead>
                                      <tr>
                                        <th style={styles.subTh}>PO</th>
                                        <th style={styles.subTh}>Contenedor</th>
                                        <th style={styles.subTh}>Cliente</th>
                                        <th style={styles.subTh}>Ruta Estatus</th>
                                        <th style={styles.subTh}>Tipo de Movimiento</th>
                                        <th style={styles.subTh}>Vertical</th>
                                        <th style={styles.subTh}>Operador</th>
                                        <th style={styles.subTh}>Unidad / Placas</th>
                                        <th style={styles.subTh}>Cita</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {l.movimientos.map((mv, mvIdx) => {
                                        const patio = mv.movimientoPatio || {};
                                        return (
                                        <tr key={mv.idMovimiento ?? mvIdx}>
                                          <td style={styles.subTd}>
                                            {mv.po || "—"}
                                            {mv.booking && (
                                              <span style={styles.subSecondaryText}>Booking: {mv.booking}</span>
                                            )}
                                          </td>
                                          <td style={styles.subTd}>
                                            {mv.contenedor || "—"}
                                          </td>
                                          <td style={styles.subTd}>
                                            {mv.cliente?.nombre || "—"}
                                          </td>
                                          <td style={styles.subTd}>
                                            {mv.rutaEstatus || "—"}
                                          </td>
                                          <td style={styles.subTd}>
                                            {mv.tipoMovimiento || "—"}
                                          </td>
                                          <td style={styles.subTd}>
                                            {mv.cliente?.vertical || "—"}
                                          </td>
                                          <td style={styles.subTd}>
                                            {patio.operadorNombre || "—"}
                                            {patio.operadorContacto && (
                                              <span style={styles.subSecondaryText}>{patio.operadorContacto}</span>
                                            )}
                                          </td>
                                          <td style={styles.subTd}>
                                            {patio.idRemolque?.nombre || "—"}
                                            {(patio.tractorPlaca || patio.remolquePlaca) && (
                                              <span style={styles.subSecondaryText}>
                                                {[patio.tractorPlaca, patio.remolquePlaca].filter(Boolean).join(" · ")}
                                              </span>
                                            )}
                                          </td>
                                          <td style={styles.subTd}>
                                            {mv.citaProgramada || "—"}
                                          </td>
                                        </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            ) : (
                              <div style={styles.subTableCard}>
                                <div style={styles.subEmpty}>
                                  No hay servicios detallados disponibles para esta línea.
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}

              {!loading && !error && rows.length === 0 && (
                <tr>
                  <td colSpan={COLSPAN} style={{ ...styles.td, ...styles.empty }}>
                    No hay líneas que coincidan con la búsqueda.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {!loading && !error && totalElements > 0 && (
          <div style={styles.footer}>
            <span>
              Mostrando {showingFrom}–{showingTo} de {fmtNum(totalElements)}
            </span>
            <div style={styles.pagNav}>
              <button
                className="lista-pag-btn"
                style={styles.pagBtn(false, page === 0)}
                disabled={page === 0}
                onClick={() => onPageChange(Math.max(0, page - 1))}
              >
                <ChevronLeft size={16} />
              </button>
              {pageWindow[0] > 0 && (
                <>
                  <button
                    className="lista-pag-btn"
                    style={styles.pagBtn(false, false)}
                    onClick={() => onPageChange(0)}
                  >
                    1
                  </button>
                  {pageWindow[0] > 1 && (
                    <span style={{ padding: "0 4px" }}>…</span>
                  )}
                </>
              )}
              {pageWindow.map((p) => (
                <button
                  key={p}
                  className="lista-pag-btn"
                  style={styles.pagBtn(p === page, false)}
                  onClick={() => onPageChange(p)}
                >
                  {p + 1}
                </button>
              ))}
              {pageWindow[pageWindow.length - 1] < safeTotalPages - 1 && (
                <>
                  {pageWindow[pageWindow.length - 1] < safeTotalPages - 2 && (
                    <span style={{ padding: "0 4px" }}>…</span>
                  )}
                  <button
                    className="lista-pag-btn"
                    style={styles.pagBtn(false, false)}
                    onClick={() => onPageChange(safeTotalPages - 1)}
                  >
                    {safeTotalPages}
                  </button>
                </>
              )}
              <button
                className="lista-pag-btn"
                style={styles.pagBtn(false, page >= safeTotalPages - 1)}
                disabled={page >= safeTotalPages - 1}
                onClick={() =>
                  onPageChange(Math.min(safeTotalPages - 1, page + 1))
                }
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {exporting && (
        <ExcelDownloadLoader manual status="PROCESS" progress={exportProgress} />
      )}
    </>
  );
};

export default ListaTransportistas;
